import { useSweepstakes } from '../../contexts/SweepstakesContext'
import { Mail, Phone, ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import Input from '../ui/Input'
import Button from '../ui/Button'

/**
 * Step3Contact
 * Third step: Collect email and phone
 */
const Step3Contact = () => {
  const {
    formData,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    errors
  } = useSweepstakes()

  const handleNext = () => {
    goToNextStep()
  }

  const handleBack = () => {
    goToPreviousStep()
  }

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '')
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)
    if (match) {
      return !match[2]
        ? match[1]
        : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`
    }
    return value
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value)
    updateFormData('phone', formatted)
  }

  return (
    <div className="space-y-6">
      {/* Question */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          How can we reach you?
        </h3>
        <p className="text-sm text-gray-600">
          We'll use this to notify you if you win!
        </p>
      </div>

      {/* Inputs */}
      <div className="space-y-4">
        <Input
          label="Email"
          type="email"
          placeholder="your@email.com"
          value={formData.email}
          onChange={(e) => updateFormData('email', e.target.value)}
          error={errors.email}
          icon={<Mail className="w-4 h-4 text-gray-400" />}
          autoFocus
          required
        />

        <Input
          label="Phone Number (Optional)"
          type="tel"
          placeholder="(555) 555-5555"
          value={formData.phone}
          onChange={handlePhoneChange}
          error={errors.phone}
          icon={<Phone className="w-4 h-4 text-gray-400" />}
          maxLength={14}
        />
      </div>

      {/* SMS/Email Opt-In */}
      <div className="space-y-3 pt-2">
        {/* Transactional Consent - Required */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.smsTransactionalConsent}
            onChange={(e) => updateFormData('smsTransactionalConsent', e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
          />
          <span className="text-xs text-gray-600 leading-relaxed">
            I agree to receive SMS and email notifications from Love & Photos LLC related to my booking, account, or services, including confirmations, reminders, updates, and support messages. Message frequency varies. Message and data rates may apply for SMS. Reply STOP to unsubscribe or HELP for help at any time.
            <span className="text-red-500 ml-1">*</span>
          </span>
        </label>
        {errors.smsTransactionalConsent && (
          <p className="text-xs text-red-500 ml-7">{errors.smsTransactionalConsent}</p>
        )}

        {/* Marketing Consent - Optional */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.smsMarketingConsent}
            onChange={(e) => updateFormData('smsMarketingConsent', e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
          />
          <span className="text-xs text-gray-600 leading-relaxed">
            I agree to receive occasional marketing and promotional messages via SMS and email from Love & Photos LLC, including offers, updates, and announcements. Message frequency varies. Message and data rates may apply for SMS. Reply STOP to unsubscribe or HELP for help at any time.
          </span>
        </label>

        {/* Privacy Policy & Terms Links */}
        <div className="text-xs text-gray-500 ml-7">
          <Link to="/privacy" className="text-purple-600 hover:text-purple-700 underline">
            Privacy Policy
          </Link>
          {' & '}
          <Link to="/terms" className="text-purple-600 hover:text-purple-700 underline">
            Terms and Conditions
          </Link>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={handleBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors"
        >
          <ChevronLeft className="w-4 h-4 inline mr-1" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors"
          disabled={!formData.email.trim()}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}

export default Step3Contact
